import React from "react";
import Parse from "parse";

import { useTranslation, AdminLayout } from "@opendash/core";
import { AdminToolbar } from "@opendash/ui";

import {
  Button,
  Divider,
  Input,
  List,
  message,
  Modal,
  Space,
  Tag,
  Tooltip,
} from "antd";
import { useParseQuery } from "parse-hooks";
import { Invoice, InvoicePage } from "../types";
import { Icon } from "@opendash/icons";
import { useNavigate } from "react-router-dom";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { ParseObjectOrderButtons } from "./ParseObjectOrderButtons";
import { $parse } from "@opendash/plugin-parse";

export const InvoiceAdminRoute = React.memo(function InvoiceAdminRoute() {
  const t = useTranslation();
  const navigate = useNavigate();

  const currentTenant = Parse.User.current()?.get("tenant");

  const [showDeleted, setShowDeleted] = React.useState(false);

  const [duplicateSource, setDuplicateSource] = React.useState<Invoice | null>(
    null
  );
  const [duplicateTitle, setDuplicateTitle] = React.useState("");
  const [duplicating, setDuplicating] = React.useState(false);

  const duplicateInvoice = React.useCallback(
    async (source: Invoice, newTitle: string) => {
      const tenant = Parse.User.current()?.get("tenant");
      if (!tenant) {
        throw new Error("no tenant on current user");
      }

      const newInvoice = new Invoice({
        tenant,
        title: newTitle,
        description: source.get("description"),
        icon: source.get("icon"),
        draft: true,
        deleted: false,
        order: 100,
      });
      await newInvoice.save();

      try {
        const sourcePages = await new Parse.Query(InvoicePage)
          .equalTo("invoice", source)
          .equalTo("deleted", false)
          .descending("order")
          .limit(1000000)
          .find();

        if (sourcePages.length === 0) {
          return newInvoice;
        }

        const newPages = sourcePages.map((src) => {
          const annotations = src.get("annotations");
          return new InvoicePage({
            tenant,
            invoice: newInvoice,
            title: src.get("title"),
            description: src.get("description"),
            image: src.get("image"),
            imageWidth: src.get("imageWidth"),
            imageHeight: src.get("imageHeight"),
            draft: true,
            deleted: false,
            order: src.get("order") ?? 100,
            annotations: annotations
              ? JSON.parse(JSON.stringify(annotations))
              : [],
          });
        });

        await Parse.Object.saveAll(newPages);
        return newInvoice;
      } catch (err) {
        try {
          const partialPages = await new Parse.Query(InvoicePage)
            .equalTo("invoice", newInvoice)
            .limit(1000000)
            .find();
          if (partialPages.length > 0) {
            await Parse.Object.destroyAll(partialPages);
          }
          await newInvoice.destroy();
        } catch {
          // cleanup best-effort; surface original error
        }
        throw err;
      }
    },
    []
  );

  const query = React.useMemo(() => {
    const query = new Parse.Query(Invoice)
      .equalTo("tenant", currentTenant)
      .descending("order")
      .limit(1000000);

    if (!showDeleted) {
      query.equalTo("deleted", false);
    }

    return query;
  }, [currentTenant?.id, showDeleted]);

  const invoices = useParseQuery(query);

  const submitDuplicate = React.useCallback(async () => {
    const source = duplicateSource;
    const title = duplicateTitle.trim();
    if (!source || title.length === 0 || duplicating) return;

    setDuplicating(true);
    try {
      await duplicateInvoice(source, title);
      message.success(t("app:admin.invoices.duplicate_success"));
      setDuplicateSource(null);
      setDuplicateTitle("");
      invoices.reload();
    } catch (err) {
      console.error("Invoice duplication failed:", err);
      message.error(t("app:admin.invoices.duplicate_error"));
    } finally {
      setDuplicating(false);
    }
  }, [duplicateSource, duplicateTitle, duplicating, duplicateInvoice, invoices, t]);

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title={t("app:admin.invoices.title")}
        description={t("app:admin.invoices.description")}
        padding={false}
        actions={[
          <Button
            key="new"
            type="primary"
            icon={<Icon icon="fa:plus" />}
            children={t("app:admin.invoices.action_new")}
            onClick={() => {
              $parse.ui.createObject("APP_Invoice").then((objectId) => {
                if (objectId) {
                  navigate(`/admin/rechnungserklaerer/invoice/${objectId}`);
                }
              });
            }}
          />,
        ]}
      />
      <div className="od-page-main">
        <List
          loading={invoices.loading}
          dataSource={invoices.result}
          renderItem={(invoice) => {
            return (
              <List.Item>
                <>
                  <List.Item.Meta
                    title={invoice.get("title")}
                    description={invoice.get("description")}
                    avatar={
                      <Tooltip
                        overlay={
                          invoice.get("deleted")
                            ? t(
                                "app:admin.invoices.visibillity_change_tooltip_deleted"
                              )
                            : invoice.get("draft")
                            ? t(
                                "app:admin.invoices.visibillity_change_tooltip_draft"
                              )
                            : t(
                                "app:admin.invoices.visibillity_change_tooltip_nodraft"
                              )
                        }
                      >
                        <Icon
                          icon={
                            invoice.get("deleted")
                              ? "fa:trash"
                              : invoice.get("draft")
                              ? "fa:eye-slash"
                              : "fa:eye"
                          }
                          style={{
                            fontSize: 20,
                            cursor: "pointer",
                            color:
                              invoice.get("deleted") || invoice.get("draft")
                                ? "red"
                                : undefined,
                          }}
                          onClick={() => {
                            if (invoice.get("deleted")) {
                            } else if (invoice.get("draft")) {
                              invoice.set("draft", false);

                              invoice
                                .save()
                                .then(() => {
                                  message.success(
                                    t(
                                      "app:admin.invoices.visibillity_change_success"
                                    )
                                  );
                                  return invoices.reload();
                                })
                                .catch(() => {
                                  message.error(
                                    t(
                                      "app:admin.invoices.visibillity_change_error"
                                    )
                                  );
                                });
                            } else {
                              invoice.set("draft", true);
                              invoice
                                .save()
                                .then(() => {
                                  message.success(
                                    t(
                                      "app:admin.invoices.visibillity_change_success"
                                    )
                                  );
                                  return invoices.reload();
                                })
                                .catch(() => {
                                  message.error(
                                    t(
                                      "app:admin.invoices.visibillity_change_error"
                                    )
                                  );
                                });
                            }
                          }}
                        />
                      </Tooltip>
                    }
                  />

                  <Space direction="horizontal">
                    {!showDeleted && (
                      <ParseObjectOrderButtons
                        objects={invoices.result}
                        current={invoice}
                        onChange={() => {
                          invoices.reload();
                        }}
                      />
                    )}

                    <span style={{ display: "inline-block", width: 20 }} />

                    {!showDeleted && (
                      <Button
                        key="edit"
                        type="primary"
                        icon={<Icon icon="fa:edit" />}
                        children={t("app:admin.invoices.action_edit")}
                        onClick={() => {
                          $parse.ui
                            .updateObject(invoice.className, invoice.id)
                            .then((saved) => {
                              if (saved) {
                                invoices.reload();
                              }
                            });
                        }}
                      />
                    )}

                    {!showDeleted && (
                      <Button
                        key="edit-pages"
                        type="primary"
                        icon={<Icon icon="fa:file-invoice" />}
                        children={t("app:admin.invoices.action_edit_pages")}
                        onClick={() => {
                          navigate(
                            `/admin/rechnungserklaerer/invoice/${invoice.id}`
                          );
                        }}
                      />
                    )}

                    {!showDeleted && (
                      <Button
                        key="duplicate"
                        type="default"
                        icon={<Icon icon="fa:copy" />}
                        children={t("app:admin.invoices.action_duplicate")}
                        onClick={() => {
                          setDuplicateSource(invoice);
                          setDuplicateTitle(
                            t("app:admin.invoices.duplicate_title_default", {
                              title: invoice.get("title") ?? "",
                            })
                          );
                        }}
                      />
                    )}

                    {invoice.get("deleted") && (
                      <Button
                        key="delete-restore"
                        type="default"
                        icon={<Icon icon="fa:trash-undo" />}
                        children={t("app:admin.invoices.action_restore")}
                        onClick={() => {
                          invoice.set("deleted", false);
                          invoice.set("draft", true);
                          invoice.set("order", 100);

                          invoice.save().then(
                            () => {
                              message.success(
                                t("app:admin.invoices.restore_success")
                              );

                              invoices.reload();
                            },
                            (error) => {
                              message.error(
                                t("app:admin.invoices.restore_error")
                              );
                            }
                          );
                        }}
                      />
                    )}

                    {invoice.get("deleted") && (
                      <Button
                        key="delete-final"
                        type="default"
                        danger
                        icon={<Icon icon="fa:trash" />}
                        children={t("app:admin.invoices.action_delete_final")}
                        onClick={() => {
                          $parse.ui
                            .deleteObject(invoice.className, invoice.id)
                            .then(() => {
                              invoices.reload();
                            });
                        }}
                      />
                    )}

                    {!invoice.get("deleted") && (
                      <Button
                        key="delete"
                        type="default"
                        danger
                        icon={<Icon icon="fa:trash" />}
                        children={t("app:admin.invoices.action_delete")}
                        onClick={() => {
                          Modal.confirm({
                            title: t("app:admin.invoices.delete_confirm_title"),
                            content: t(
                              "app:admin.invoices.delete_confirm_message"
                            ),
                            okText: t("app:admin.invoices.delete_confirm_ok"),
                            okType: "danger",
                            cancelText: t(
                              "app:admin.invoices.delete_confirm_cancel"
                            ),
                            onOk() {
                              invoice.set("deleted", true);
                              invoice.set("order", 0);

                              invoice.save().then(
                                () => {
                                  message.success(
                                    t("app:admin.invoices.delete_success")
                                  );

                                  invoices.reload();
                                },
                                (error) => {
                                  message.error(
                                    t("app:admin.invoices.delete_error")
                                  );
                                }
                              );
                            },
                          });
                        }}
                      />
                    )}
                  </Space>
                </>
              </List.Item>
            );
          }}
        />

        <Divider />

        <Checkbox
          checked={showDeleted}
          onChange={(e) => {
            setShowDeleted(e.target.checked);
          }}
        >
          {t("app:admin.invoices.action_show_deleted")}
        </Checkbox>
      </div>

      <Modal
        open={!!duplicateSource}
        title={t("app:admin.invoices.duplicate_modal_title")}
        okText={t("app:admin.invoices.duplicate_modal_ok")}
        cancelText={t("app:admin.invoices.duplicate_modal_cancel")}
        confirmLoading={duplicating}
        okButtonProps={{ disabled: duplicateTitle.trim().length === 0 }}
        onCancel={() => {
          if (duplicating) return;
          setDuplicateSource(null);
          setDuplicateTitle("");
        }}
        onOk={submitDuplicate}
        destroyOnClose
      >
        <p>{t("app:admin.invoices.duplicate_modal_message")}</p>
        <Input
          autoFocus
          value={duplicateTitle}
          onChange={(e) => setDuplicateTitle(e.target.value)}
          onPressEnter={() => {
            submitDuplicate();
          }}
          placeholder={t("app:admin.invoices.duplicate_title_placeholder")}
          maxLength={200}
        />
      </Modal>
    </AdminLayout>
  );
});
